<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="store.isOpen" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" @click.self="handleBackdropClick">
        <div class="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">

          <!-- Header with Icon -->
          <div class="p-6 pb-0 flex items-start">
            <div :class="iconBgClass" class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <!-- Dynamic Icons -->
              <svg v-if="store.type === 'error'" class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <svg v-else-if="store.type === 'success'" class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
              <svg v-else-if="store.type === 'confirm'" class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <svg v-else class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">{{ store.title }}</h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500 whitespace-pre-wrap">{{ store.message }}</p>
              </div>
              <!-- Prompt Input -->
              <div v-if="store.type === 'prompt'" class="mt-4">
                <input
                    v-model="store.inputValue"
                    :placeholder="store.inputPlaceholder"
                    ref="promptInput"
                    @keyup.enter="store.handleConfirm"
                    class="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 flex flex-col sm:flex-row-reverse gap-3 mt-4">
            <button
                type="button"
                @click="store.handleConfirm"
                :class="confirmButtonClass"
                class="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm transition"
            >
              {{ store.confirmText }}
            </button>

            <button
                v-if="store.type === 'confirm' || store.type === 'prompt'"
                type="button"
                @click="store.handleCancel"
                class="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm transition"
            >
              {{ store.cancelText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, nextTick, watch } from 'vue'
import { useModalStore } from '@/stores/modal'

const store = useModalStore()

// Focus input on prompt open
watch(() => store.isOpen, async (newVal) => {
  if (newVal && store.type === 'prompt') {
    await nextTick()
    const input = document.querySelector('.bg-white input')
    if (input) input.focus()
  }
})

const iconBgClass = computed(() => {
  switch (store.type) {
    case 'error': return 'bg-red-100'
    case 'success': return 'bg-green-100'
    case 'confirm': return 'bg-yellow-100'
    default: return 'bg-blue-100'
  }
})

const confirmButtonClass = computed(() => {
  switch (store.type) {
    case 'error': return 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    case 'success': return 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
    case 'confirm': return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
    case 'prompt': return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
    default: return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
  }
})

const handleBackdropClick = () => {
  if (store.type !== 'prompt') {
    store.handleCancel()
  }
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .bg-white,
.modal-fade-leave-active .bg-white {
  transition: transform 0.2s ease-out;
}

.modal-fade-enter-from .bg-white,
.modal-fade-leave-to .bg-white {
  transform: scale(0.95);
}
</style>
